import { BALANCE } from '../balance';
import type { Condition } from '../schemas/progression';
import type { NpcDefInput } from '../schemas/progressionDefs';

/**
 * Neighbors and Boss Cat (content-schemas §5.1 + §16.4, economy-progression §7, story-dialogue §7–§8).
 *
 * Talk selection (content-schemas §14.10): filter by conditions, prefer unseen `once`, highest priority, then a
 * seeded random pick that avoids the last dialogue. Every everyday line shares one priority so the pick stays
 * varied; greet_lv<N> lines unlock from friendship level N and `[sau ch4]` idle lines after chapter 4.
 * Level-up scenes are `levels[].thanksDialogueId`; home-visit scenes follow `dlg_<npcId>_visit_home_*`.
 */
type Talk = NpcDefInput['talk'][number];
type HumanNpc = 'npc_lan' | 'npc_bao' | 'npc_mit';

const npcStars = { type: 'stars', amount: BALANCE.starsPer.friendshipLevel.npc } as const;
const petStars = { type: 'stars', amount: BALANCE.starsPer.friendshipLevel.pet } as const;
const afterCh4: Condition[] = [{ type: 'chapterSeen', chapterId: 'ch4' }];

function lines(
  npc: HumanNpc,
  context: string,
  count: number,
  conditions: (n: number) => Condition[] = () => [],
): Talk[] {
  return Array.from({ length: count }, (_, i) => ({
    dialogueId: `dlg_${npc}_${context}_${String(i + 1)}` as const,
    conditions: conditions(i + 1),
    priority: 0,
  }));
}

/** greet_lv0..3 (3 lines each) + daily (5) + idle (`[sau ch4]` lines gated). */
function everydayTalk(npc: HumanNpc, idleCount: number, idleAfterCh4: readonly number[]): Talk[] {
  return [
    ...[0, 1, 2, 3].flatMap((lv) =>
      lines(npc, `greet_lv${String(lv)}`, 3, () =>
        lv === 0 ? [] : [{ type: 'friendshipAtLeast', npcId: npc, level: lv }],
      ),
    ),
    ...lines(npc, 'daily', 5),
    ...lines(npc, 'idle', idleCount, (n) => (idleAfterCh4.includes(n) ? afterCh4 : [])),
  ];
}

export const NPCS = [
  {
    id: 'npc_lan',
    kind: 'human',
    nameKey: 'npc.npc_lan.name',
    portrait: 'por_npc_lan_neutral',
    sprite: 'chr_npc_lan',
    favorites: {
      categories: ['snack', 'garden'],
      itemIds: ['prod_green_tea', 'prod_milk_tea', 'prod_cookie_box', 'prod_seed_packet'],
    },
    thresholdsRef: 'npc',
    levels: [
      {
        level: 1,
        rewards: [npcStars, { type: 'item', itemId: 'dec_monstera', qty: 1 }],
        thanksDialogueId: 'dlg_npc_lan_lvup1',
      },
      {
        level: 2,
        rewards: [npcStars, { type: 'avatarPart', partId: 'av_hat_flower' }],
        thanksDialogueId: 'dlg_npc_lan_lvup2',
      },
      {
        level: 3,
        rewards: [npcStars, { type: 'coins', amount: 100 }, { type: 'title', titleId: 'ttl_lan_bestie' }],
        thanksDialogueId: 'dlg_npc_lan_lvup3',
      },
    ],
    talk: everydayTalk('npc_lan', 9, [8, 9]),
    orderTemplates: [
      'ord_first_lan',
      'ord_lan_tea_party',
      'ord_lan_green_tea',
      'ord_lan_garden',
      'ord_lan_honey_jam',
      'ord_lan_visit_friend',
      'ord_lan_home_jam',
    ],
    firstOrderTemplateId: 'ord_first_lan',
    metFlag: 'flag_npc_met_npc_lan',
    canShop: true,
    letterIds: ['ltr_npc_lan_1', 'ltr_npc_lan_2', 'ltr_npc_lan_3'],
  },
  {
    id: 'npc_bao',
    kind: 'human',
    nameKey: 'npc.npc_bao.name',
    portrait: 'por_npc_bao_neutral',
    sprite: 'chr_npc_bao',
    favorites: {
      categories: ['food', 'stationery'],
      itemIds: ['prod_instant_noodle', 'prod_canned_coffee'],
    },
    thresholdsRef: 'npc',
    levels: [
      {
        level: 1,
        rewards: [npcStars, { type: 'coins', amount: 50 }],
        thanksDialogueId: 'dlg_npc_bao_lvup1',
      },
      {
        level: 2,
        rewards: [npcStars, { type: 'item', itemId: 'dec_mug_deadline', qty: 1 }],
        thanksDialogueId: 'dlg_npc_bao_lvup2',
      },
      {
        level: 3,
        rewards: [
          npcStars,
          { type: 'item', itemId: 'fur_office_chair', qty: 1 },
          { type: 'title', titleId: 'ttl_honorary_colleague' },
        ],
        thanksDialogueId: 'dlg_npc_bao_lvup3',
      },
    ],
    talk: everydayTalk('npc_bao', 9, [9]),
    orderTemplates: [
      'ord_first_bao',
      'ord_bao_deadline_fuel',
      'ord_bao_midnight',
      'ord_bao_snack_meeting',
      'ord_bao_monday',
      'ord_bao_notebook',
      'ord_bao_home_noodle',
      'ord_bao_healthy_try',
      'ord_bao_laptop_sticker',
      'ord_bao_candle',
    ],
    firstOrderTemplateId: 'ord_first_bao',
    metFlag: 'flag_npc_met_npc_bao',
    canShop: true,
    letterIds: ['ltr_npc_bao_1', 'ltr_npc_bao_2', 'ltr_npc_bao_3'],
  },
  {
    id: 'npc_mit',
    kind: 'human',
    nameKey: 'npc.npc_mit.name',
    portrait: 'por_npc_mit_neutral',
    sprite: 'chr_npc_mit',
    favorites: {
      categories: ['snack'],
      itemIds: ['prod_candy_jar', 'prod_sticker_pack', 'prod_gel_pens', 'prod_pastel_notebook'],
    },
    thresholdsRef: 'npc',
    levels: [
      {
        level: 1,
        rewards: [
          npcStars,
          { type: 'sticker', stickerId: 'stk_mit_1' },
          { type: 'sticker', stickerId: 'stk_mit_2' },
          { type: 'sticker', stickerId: 'stk_mit_3' },
          { type: 'sticker', stickerId: 'stk_mit_4' },
          { type: 'sticker', stickerId: 'stk_mit_5' },
        ],
        thanksDialogueId: 'dlg_npc_mit_lvup1',
      },
      {
        level: 2,
        rewards: [npcStars, { type: 'avatarPart', partId: 'av_hat_cat_beanie' }],
        thanksDialogueId: 'dlg_npc_mit_lvup2',
      },
      {
        level: 3,
        rewards: [
          npcStars,
          { type: 'photoFrame', frameId: 'frm_vo_o_li' },
          { type: 'title', titleId: 'ttl_best_shopkeeper' },
        ],
        thanksDialogueId: 'dlg_npc_mit_lvup3',
      },
    ],
    talk: everydayTalk('npc_mit', 9, [9]),
    orderTemplates: [
      'ord_first_mit',
      'ord_mit_candy_share',
      'ord_mit_reward',
      'ord_mit_sticker_hunt',
      'ord_mit_school',
      'ord_mit_cat_gift',
      'ord_mit_home_kimbap',
      'ord_mit_science',
    ],
    firstOrderTemplateId: 'ord_first_mit',
    metFlag: 'flag_npc_met_npc_mit',
    canShop: true,
    letterIds: ['ltr_npc_mit_1', 'ltr_npc_mit_2', 'ltr_npc_mit_3'],
  },
  {
    id: 'npc_boss_cat',
    kind: 'pet',
    nameKey: 'npc.npc_boss_cat.name',
    portrait: 'por_npc_boss_cat_neutral',
    sprite: 'chr_boss_cat_sit',
    favorites: { categories: ['pet'], itemIds: ['prod_cat_pate'] },
    thresholdsRef: 'pet',
    levels: [
      {
        level: 1,
        rewards: [petStars, { type: 'item', itemId: 'fur_cat_bed', qty: 1 }],
        thanksDialogueId: 'dlg_cat_lvup1',
      },
      {
        level: 2,
        rewards: [petStars, { type: 'sticker', stickerId: 'stk_boss_cat_approved' }],
        thanksDialogueId: 'dlg_cat_lvup2',
      },
    ],
    // Taps 1–12 (random, no immediate repeat). dlg_cat_tap_13 is for taps after the daily petting cap.
    talk: Array.from({ length: 12 }, (_, i) => ({
      dialogueId: `dlg_cat_tap_${String(i + 1)}` as const,
      conditions: [],
      priority: 0,
    })),
    orderTemplates: [],
    metFlag: 'flag_npc_met_npc_boss_cat',
    canShop: false,
    letterIds: [],
  },
] as const satisfies readonly NpcDefInput[];
